import React, { useEffect, useState, useCallback, useMemo } from "react";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';


import { Row, Col } from "stories/layout";
import { Input, Toggle } from "stories/forms";
import { Card, Typography, Button, Link, IconButton, Modal, Spinner, BreadcrumbsDynamic } from "stories/components";
import { PageHeader, PageContainer } from "components/Page"
import { TableAuto } from "stories/tables"
import { useAppContext } from "contexts/AppContext"
import { round, roundScaleDecimals } from "services/helpers"

import PortfolioEdit from "views/app/PortfolioEdit/PortfolioEdit"
import SplitTransactionEdit from "views/app/SplitTransactionEdit/SplitTransactionEdit"
import DividendTransactionsGroupCreate from "views/app/DividendTransactionsGroupCreate/DividendTransactionsGroupCreate"

const UnmadeSplitTransactionsList = (props) => {
  
  const { t } = useTranslation();
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  const unmadeDividendTransactions = useStoreState((state) => state.unmadeDividendTransactions?.unmadeDividendTransactions);
  const getUnmadeDividendTransactions = useStoreActions((actions) => actions.unmadeDividendTransactions.getUnmadeDividendTransactions);
  
  // Add account and portfolio state for filtering
  const account = useStoreState((state) => state.myUser?.account);
  // Dividends of every active portfolio; the portfolio is shown on the row when there are
  // several (#1409). The endpoint returns all portfolios.
  const activePortfolios = useStoreState((state) => state.portfolio?.activePortfolios);
  const activeKey = useStoreState((state) => state.portfolio?.activeKey);
  const activeIds = useMemo(() => new Set((activePortfolios || []).map((p) => p.id)), [activeKey]);
  const showPortfolio = activeIds.size > 1;

  const { getPortfolioName, getUserTypeName, getTransactionType } = useAppContext()
  
  const setPortfolio = useStoreActions((actions) => actions.portfolio.setPortfolio);

  const loading = useStoreState((state) => state.unmadeDividendTransactions?.loading);
  const count = useStoreState((state) => state.portfolio?.count);
  const getPortfolios = useStoreActions((actions) => actions.portfolio.getPortfolios);


  
  const [filters, setFilters] = useState({
    limit: 30,
    offset:0,
  })

  const showOlder = useStoreState((state) => Boolean(state.unmadeDividendTransactions?.showOlder));
  const setShowOlder = useStoreActions((actions) => actions.unmadeDividendTransactions.setShowOlder);
  
  const handleChange = ({target}) => {
    setFilters({
      ...filters,
      [target.name]: target.value,
    })
  }
  
  const paginationPrevious = () => {
    let offset = filters?.offset - filters?.limit;
    if (offset < 0) {
      offset = 0;
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }
  
  const paginationNext = () => {
    let offset = filters?.offset + filters?.limit;
    if (offset > filters?.count) {
      return
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }
  
  const headers = [

      // { label: t("unmade_split_transactions_list_batch", "Erä id"), key: "batch"},
      { label: t("unmade_dividend_transactions_list_dividend_id", "ID"), key: "dividend_id"},
      { label: t("unmade_dividend_transactions_list_instrument_name", "Instrumentti"), key: "instrument_name"},
      // { label: t("unmade_dividend_transactions_list_batch_purchase_date", "Hankintapäivä"), key: "batch_purchase_date"},
      // { label: t("unmade_dividend_transactions_list_batch_balance", "erän koko ennen splittiä"), key: "batch_balance"},
      { label: t("unmade_dividend_transactions_list_ex_date", "Irtoamispäivä"), key: "ex_date"},
      { label: t("unmade_dividend_transactions_list_pay_date", "Maksupäivä"), key: "pay_date"},
      { label: t("unmade_dividend_transactions_list_dividend", "Osinko/osake"), key: "original_rate"},
      { label: t("unmade_dividend_transactions_list_currency_label", "Valuutta"), key: "currency_label"},
      // { label: t("unmade_dividend_transactions_list_portfolio", "Salkku"), key: "portfolio"},
      ...(showPortfolio ? [{ label: t("transactions_list_portfolio", "Salkku"), key: "portfolio_names"}] : []),

    { label: t("portfolios_list_actions", " "), key: "actions"},
  ];

  const passPortfolio  = (id) => {
    // get query params id
      console.log("id", id)
      const filtered = portfolios.filter(o => o.id == id)
      if (filtered && filtered.length > 0) {
        setPortfolio(filtered[0])
      }
  }

  // Reload dividends when the active portfolios or the show-older toggle change
  useEffect(() => {
    if (activeKey) {
      getUnmadeDividendTransactions({ showOlder });
    }
  }, [activeKey, showOlder])

  
  
  
  const makeDuplicateKey = (d) => {
    if (!d) return null;
    const exDay = d.ex_date ? String(d.ex_date).slice(0, 10) : '';
    return `${d.related_model_type}|${d.related_model_id}|${exDay}|${d.rate}`;
  };

  const duplicateKeys = useMemo(() => {
    const seen = new Map();
    (unmadeDividendTransactions || []).forEach(item => {
      const key = makeDuplicateKey(item?.dividends);
      if (!key) return;
      const types = seen.get(key) || new Set();
      if (item?.dividends?.type != null) {
        types.add(item.dividends.type);
      }
      seen.set(key, types);
    });
    const dupKeys = new Set();
    seen.forEach((types, key) => {
      if (types.size > 1) dupKeys.add(key);
    });
    return dupKeys;
  }, [unmadeDividendTransactions]);

  const getTableRows = useCallback(() => {

    let data = unmadeDividendTransactions;

    // Filter by the active portfolios (ALWAYS for dividends to show correct currency)
    if (activeIds.size > 0) {
      data = unmadeDividendTransactions.filter(o => activeIds.has(Number(o.batch.portfolio)));
    }

    const organizedData = {};

    // Iterate over the original data crouping by the split id
    for (const item of data) {
      const splitId = item.dividends.id;

      if (organizedData[splitId]) {
        organizedData[splitId].push(item);
      } else {
        organizedData[splitId] = [item];
      }
    }

    const organizedDataArray = Object.values(organizedData);

    return unmadeDividendTransactions && organizedDataArray.map((row, index) => {
      let batch_balance = Number(row[0]?.batch?.batch_balance)
      const isDuplicate = duplicateKeys.has(makeDuplicateKey(row[0]?.dividends));
      const batchForModal = row
      return {
        ...row,
        _modalBatch: batchForModal,
        dividend_id: row[0]?.dividends?.id,
        instrument_name: (
          <Typography variant="h5" className="text-dark">
            {row[0]?.batch?.related_model?.name}
            {isDuplicate && (
              <span
                className="badge badge-warning ml-2"
                title={t("dividend_duplicate_type_warning", "Sama osinko löytyy sekä julkisena että yksityisenä — älä kirjaa molempia")}
              >
                !
              </span>
            )}
          </Typography>
        ),
        pay_date: moment(row[0]?.dividends?.pay_date).format('DD.MM.YYYY'),
        ex_date: moment(row[0]?.dividends?.ex_date).format('DD.MM.YYYY'),
        original_rate: {
            content: roundScaleDecimals(row[0]?.dividends?.rate, 4),
            className: "text-right"
        },
        currency_label: row[0]?.dividends?.currency_label,
        portfolio_names: [...new Set(row.map((item) => item?.batch?.portfolio))].map((id) => getPortfolioName(Number(id))).join(", "),
        actions: (
          <div className="d-flex justify-content-end">

          <Button name="process_dividend_transactions_button" className="btn-sm btn-action" color="secondary" onClick={() => openModalEdit(row)}>{t("process_dividend_transactions_button","Käsittele")}</Button>
          </div>
        ),
      }
    })

    

    // return unmadeSplitTransactions && unmadeSplitTransactions.map((row, index) => {
    //   let batch_balance = Number(row?.batch?.batch_balance)
    //   return {
    //     ...row,
    //     batch: row?.batch?.batch_id? row?.batch?.batch_id: row?.batch?.id,
    //     portfolio: getPortfolioName(row?.batch?.portfolio),
    //     instrument_name: <Typography variant="h5" className="text-dark">{row?.batch?.related_model?.name}</Typography>,
    //     batch_balance: batch_balance,
    //     dividend: row?.splits?.dividend,
    //     split_id: row?.splits?.id,
    //     date_effective: moment(row?.splits?.date_effective).format('DD.MM.YYYY'),
    //     batch_purchase_date: moment(row?.batch?.batch_purchase_date).format('DD.MM.YYYY'),
    //     actions: (
    //       <div className="d-flex justify-content-end">

    //         <Button className="btn-sm " color="danger" onClick={() => openModalEdit(row)}>{t("Käsittele")}</Button>
    //       </div>
    //     ),
    //   }
    // })
  }, [unmadeDividendTransactions, account, activeIds, duplicateKeys, t])
  
  // Modal: New/Edit portfolio
  
  const [modalEditOpen, setModalEditOpen] = useState(false)
  const [modalEditBatch, setModalEditBatch] = useState()
  
  const toggleModalEdit = () => {
    setModalEditOpen(s => !s);
  }
  
  const openModalEdit = (batch) => {
    setModalEditOpen(true)
    setModalEditBatch(batch);
  }
  
  const modalEditResolve = () => {
    setModalEditOpen(false);
    // setModalEditBatch(null);
    getUnmadeDividendTransactions({ showOlder })
    props.getTransactions()
    // props.setParentFilters({
    //   ...filters,
    //   update: props.parentfilters.update? true:false
    // })

  }
  
  const modalEditReject = () => {
    setModalEditOpen(false);
    setModalEditBatch(null);
  }
  
  return (
    <>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <PageHeader title={t("unmade_dividend_transactions_list_title","Käsittele osingot")} />
          <div className="d-flex align-items-center ml-md-auto">
            <i className="fas fa-sort-amount-down mr-2" aria-hidden="true" />
            <Typography variant="h5" className="mb-0">{t("unmade_dividend_transactions_list_description","Vanhimmat ensin maksupäivän mukaan")}</Typography>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div
            className="sk-toggle-field"
            role="group"
            aria-label={t("unmade_dividends_show_all", "Näytä myös vanhat")}
          >
            <Toggle
              checked={showOlder}
              onChange={() => setShowOlder(!showOlder)}
              labelOn=""
              labelOff=""
            />
            <button
              type="button"
              className="sk-toggle-field__label"
              onClick={() => setShowOlder(!showOlder)}
            >
              {t("unmade_dividends_show_all", "Näytä myös vanhat")}
            </button>
          </div>
        </div>

        <TableAuto
        showRowNumber={false}
        showId={false}
        checkboxes={false}
        headers={headers}
        headerColor={"light"}
        rows={getTableRows()}
        loading={loading}
        pagination={false}
        paginationPrevious={paginationPrevious}
        paginationNext={paginationNext}
        paginationOffset={filters?.offset}
        paginationLimit={filters?.limit}
        paginationCount={count}
        setFilters={setFilters}
        name={"unmadeSplitTransactions"}
        onRowClick={(r) => openModalEdit(r._modalBatch)}
        />
        
        <Modal
          title={t("portfolios_list_portfolio_title","Salkku")}
          showHeader={false}
          isOpen={modalEditOpen}
          toggleModal={toggleModalEdit}
          size="modal-xl"
        >
          <DividendTransactionsGroupCreate
            object={modalEditBatch}
            handleResolve={modalEditResolve}
            handleReject={modalEditReject}
          />
        </Modal>

    </>
  );
}

export default UnmadeSplitTransactionsList;
